import { useTranslation } from '../i18n/useTranslation';
import { useEffect, useRef, useCallback } from 'react';
import {
  createChart,
  LineSeries,
  AreaSeries,
  ColorType,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { CandleData } from '../types/gateway';

interface PnLPanelProps {
  candles: CandleData[];
  className?: string;
}

const GRID_COLOR = '#1E293B';
const TEXT_COLOR = '#7A8C9E';
const PRIMARY_GREEN = '#01E599';
const TERMINAL_RED = '#FF3366';
const TERMINAL_RED_FILL = 'rgba(255, 51, 102, 0.30)';

function computeEquity(candles: CandleData[]): { time: number; value: number }[] {
  if (candles.length === 0) return [];
  const basePrice = candles[0].close;
  return candles.map((c) => ({
    time: c.time,
    value: ((c.close - basePrice) / basePrice) * 100,
  }));
}

function computeDrawdown(
  equity: { time: number; value: number }[]
): { time: number; value: number }[] {
  let peak = -Infinity;
  return equity.map((e) => {
    peak = Math.max(peak, e.value);
    return { time: e.time, value: e.value - peak };
  });
}

export default function PnLPanel({ candles, className }: PnLPanelProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const equitySeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const drawdownSeriesRef = useRef<ISeriesApi<'Area'> | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const initializedRef = useRef(false);

  const initChart = useCallback(() => {
    const container = containerRef.current;
    if (!container || initializedRef.current) return;

    initializedRef.current = true;

    const chart = createChart(container, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: TEXT_COLOR,
      },
      grid: {
        vertLines: { color: GRID_COLOR },
        horzLines: { color: GRID_COLOR },
      },
      rightPriceScale: { borderColor: GRID_COLOR },
      timeScale: {
        borderColor: GRID_COLOR,
        timeVisible: true,
        secondsVisible: false,
      },
      width: container.clientWidth,
      height: container.clientHeight,
    });

    const equitySeries = chart.addSeries(LineSeries, {
      color: PRIMARY_GREEN,
      lineWidth: 2,
      priceFormat: { type: 'percent' },
    });

    const drawdownSeries = chart.addSeries(AreaSeries, {
      lineColor: TERMINAL_RED,
      topColor: TERMINAL_RED_FILL,
      bottomColor: 'rgba(255, 51, 102, 0.00)',
      lineWidth: 1,
      priceScaleId: 'drawdown',
      priceFormat: { type: 'percent' },
    });

    chart.priceScale('drawdown').applyOptions({
      scaleMargins: { top: 0.65, bottom: 0 },
    });

    chartRef.current = chart;
    equitySeriesRef.current = equitySeries;
    drawdownSeriesRef.current = drawdownSeries;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry && chartRef.current) {
        const { width, height } = entry.contentRect;
        chartRef.current.applyOptions({ width, height });
      }
    });

    observer.observe(container);
    resizeObserverRef.current = observer;
  }, []);

  useEffect(() => {
    initChart();

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
      equitySeriesRef.current = null;
      drawdownSeriesRef.current = null;
      initializedRef.current = false;
    };
  }, [initChart]);

  useEffect(() => {
    const equitySeries = equitySeriesRef.current;
    const drawdownSeries = drawdownSeriesRef.current;
    const chart = chartRef.current;

    if (!equitySeries || !drawdownSeries || !chart) return;

    const sorted = [...candles].sort((a, b) => a.time - b.time);
    const equity = computeEquity(sorted);
    const drawdown = computeDrawdown(equity);

    equitySeries.setData(
      equity.map((e) => ({ time: e.time as UTCTimestamp, value: e.value }))
    );

    drawdownSeries.setData(
      drawdown.map((d) => ({ time: d.time as UTCTimestamp, value: d.value }))
    );

    if (sorted.length > 0) {
      chart.timeScale().fitContent();
    }
  }, [candles]);

  return (
    <div
      className={`flex flex-col h-full w-full bg-card rounded-sm border border-border${className ? ` ${className}` : ''}`}
    >
      {/* Panel header */}
      <div className="px-3 py-1.5 border-b border-border flex items-center shrink-0">
        <span className="text-[11px] font-mono text-muted-foreground tracking-widest">
          {t('pnl.header')}
        </span>
      </div>

      {/* Chart container */}
      <div
        ref={containerRef}
        className="flex-1 w-full min-h-0"
        aria-label="PnL equity and drawdown chart"
        role="img"
      />
    </div>
  );
}
